import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader/root';
import { compose } from 'redux';

import { TrackEventContext } from 'common/containers/EventContainer';
import LandingPublicRoadmapHelmet from 'common/helmets/LandingPublicRoadmapHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import withContexts from 'common/util/withContexts';
import FAQs from 'common/www/Faqs';
import LandingCTAButtons from 'common/www/LandingCTAButtons';
import LandingFeaturesCTA from 'common/www/LandingFeaturesCTA';
import Point1 from 'img/landing/public-roadmap-1.webp';
import Point2 from 'img/landing/public-roadmap-2.webp';
import Point3 from 'img/landing/public-roadmap-3.webp';
import Hero from 'img/landing/public-roadmap-hero.webp';

import 'css/components/www/_LandingSolutionsPage.scss';

class LandingPublicRoadmap extends Component {
  static propTypes = {
    trackEvent: PropTypes.func,
  };

  componentDidMount() {
    const { trackEvent } = this.props;
    trackEvent('Viewed Public Roadmap Page');
  }

  render() {
    return (
      <div className="landingPublicRoadmap landingSolutionsPage">
        <LandingPublicRoadmapHelmet />
        <div className="fullWidthContainer">
          <div className="header">
            <div className="left">
              <div className="text">
                <h1>Build a public roadmap for your&nbsp;product</h1>
                <p className="subheading">
                  Share your roadmap to show off what your team is working on and collect
                  valuable&nbsp;feedback.
                </p>
                <LandingCTAButtons renderDemoButton />
              </div>
            </div>
            <LazyLoadedImage loading="eager" src={Hero} alt="Public roadmap graphic" />
          </div>
        </div>
        <div className="mainContentContainer">
          <h2>
            Canny includes a simple public roadmap to engage your customers with
            upcoming&nbsp;features
          </h2>
          <div className="point">
            <LazyLoadedImage loading="eager" src={Point1} alt="Public roadmap" />
            <div className="text">
              <h2 className="title">Show your commitment with a public&nbsp;roadmap</h2>
              <p>Be transparent about being customer&#8209;driven.</p>
              <ul>
                <li>Collect feedback and let everyone know what you’ll be working&nbsp;on</li>
                <li>Show what your team is working on to improve the&nbsp;product</li>
              </ul>
            </div>
          </div>
          <div className="point">
            <LazyLoadedImage src={Point2} alt="Status updates in a public roadmap" />
            <div className="text">
              <h2 className="title">Keep everyone in the&nbsp;loop</h2>
              <p>
                Your stakeholders deserve to know what’s coming. Use a public roadmap to show your
                progress to the&nbsp;world.
              </p>
              <ul>
                <li>Notify customers about new features to drive&nbsp;engagement</li>
                <li>Let potential customers know you’re committed to constant&nbsp;improvement</li>
                <li>Show teammates what's coming up from&nbsp;product</li>
              </ul>
            </div>
          </div>
          <div className="point">
            <LazyLoadedImage src={Point3} alt="Increased satisfaction graph" />
            <div className="text">
              <h2 className="title">Increase transparency and satisfaction</h2>
              <p>
                Utilizing feedback is a great way to show you care. Build trust and confidence to
                drive retention and gain&nbsp;promoters.
              </p>
              <ul>
                <li>Make your roadmap public to build a brand around&nbsp;transparency</li>
                <li>Increase NPS&nbsp;scores</li>
              </ul>
            </div>
          </div>
          <LandingFeaturesCTA />
          <LandingFooterCTA style="solid" expanded={true} />
          <FAQs
            faqs={[
              {
                question: 'Can I build roadmaps for multiple products and features?',
                answer:
                  '<p>Definitely! Your product team can build as many roadmaps as you need, and as many feedback boards as you need. You can also associate each product owner or product manager with specific boards and roadmaps so they only see activity on the ones that matter to them.</p>',
                html: (
                  <p>
                    Definitely! Your product team can build as many roadmaps as you need, and as
                    many feedback boards as you need. You can also associate each product owner or
                    product manager with specific boards and roadmaps so they only see activity on
                    the ones that matter to them.
                  </p>
                ),
              },
              {
                question: 'Is Canny just a product roadmap tool?',
                answer:
                  "<p>Nope! While roadmapping is a big feature for us, we offer a full suite of product management tools. We can help you collect user feedback, manage it, analyze it, prioritize new features, build your public roadmap, and manage your changelog. You're covered for the entire feedback cycle!</p>",
                html: (
                  <p>
                    Nope! While roadmapping is a big feature for us, we offer a full suite of
                    product management tools. We can help you collect user feedback, manage it,
                    analyze it, prioritize new features, build your public roadmap, and manage your
                    changelog. You're covered for the entire feedback cycle!
                  </p>
                ),
              },
              {
                question: 'How does the roadmap planner work?',
                answer:
                  '<p>Once you\'ve set up your roadmap, you can add each new feature you plan to build to it. Canny also lets you add any ideas from your feedback board to a visual roadmap you build. This lets your stakeholders see what\'s being worked on next and understand your product plan. For more detailed info, check out our <a target="_blank" href="https://help.canny.io/en/articles/3828148-end-user-roadmap">help doc</a>.</p>',
                html: (
                  <p>
                    Once you've set up your roadmap, you can add each new feature you plan to build
                    to it. Canny also lets you add any ideas from your feedback board to a visual
                    roadmap you build. This lets your stakeholders see what's being worked on next
                    and understand your product plan. For more detailed info, check out our{' '}
                    <a
                      target="_blank"
                      href="https://help.canny.io/en/articles/3828148-end-user-roadmap"
                      rel="noopener">
                      help doc
                    </a>
                    .
                  </p>
                ),
              },
              {
                question: 'How much does it cost?',
                answer:
                  "<p>We have plans to suit most companies' requirements and budgets. You can get started with our completely free product roadmap tools (actually!) and use them until you need more powerful features and integrations. Then, you can look at our Startup, Growth, or Business plans.</p>",
                html: (
                  <p>
                    We have plans to suit most companies' requirements and budgets. You can get
                    started with our completely free product roadmap tools (actually!) and use them
                    until you need more powerful features and integrations. Then, you can look at
                    our Startup, Growth, or Business plans.
                  </p>
                ),
              },
              {
                question: 'How many team members can use Canny?',
                answer:
                  '<p>As many as you want! All Canny plans include unlimited contributors, so add everyone that you think can help you reach your product goals. While product teams are usually the most involved, adding team members from customer success, design, sales, software development, and project management is common (and very useful!).</p>',
                html: (
                  <p>
                    As many as you want! All Canny plans include unlimited contributors, so add
                    everyone that you think can help you reach your product goals. While product
                    teams are usually the most involved, adding team members from customer success,
                    design, sales, software development, and project management is common (and very
                    useful!).
                  </p>
                ),
              },
            ]}
          />
        </div>
      </div>
    );
  }
}

export default compose(withContexts({ trackEvent: TrackEventContext }), hot)(LandingPublicRoadmap);
